import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import {
  CLINIC_SPECIALTIES,
  CLINIC_TYPES,
  PROVIDER_GROUP_STATUSES,
  PROVIDER_NETWORK_SORT_BY,
} from '@backend/db/const/db.const.ENUMS';

import { ClinicsPage } from '@frontend/features/provider-network/pages/ClinicsPage';

const clinicsSearchSchema = z.object({
  // View mode (already exists)
  view: z.enum(['table', 'map']).default('table'),

  // Search query
  search: z.string().optional(),

  // Group filters (arrays) - handle single value or array from URL
  groupNames: z.preprocess((val) => {
    if (val === undefined || val === null) return undefined;
    return Array.isArray(val) ? val : [val];
  }, z.array(z.string()).optional()),
  excludeGroupNames: z.preprocess((val) => {
    if (val === undefined || val === null) return undefined;
    return Array.isArray(val) ? val : [val];
  }, z.array(z.string()).optional()),

  // Individual clinic flags
  includeIndividualClinics: z
    .union([z.boolean(), z.string()])
    .transform((val) => (typeof val === 'string' ? val === 'true' : val))
    .optional(),
  excludeIndividualClinics: z
    .union([z.boolean(), z.string()])
    .transform((val) => (typeof val === 'string' ? val === 'true' : val))
    .optional(),

  // Clinic type and specialty filters - handle single value or array from URL
  clinicTypes: z.preprocess(
    (val) => {
      if (val === undefined || val === null) return undefined;
      return Array.isArray(val) ? val : [val];
    },
    z.array(z.enum(CLINIC_TYPES)).optional(),
  ),
  specialties: z.preprocess(
    (val) => {
      if (val === undefined || val === null) return undefined;
      return Array.isArray(val) ? val : [val];
    },
    z.array(z.enum(CLINIC_SPECIALTIES)).optional(),
  ),

  // State filter - handle single value or array from URL
  states: z.preprocess((val) => {
    if (val === undefined || val === null) return undefined;
    return Array.isArray(val) ? val : [val];
  }, z.array(z.string()).optional()),

  // Address verified filter - handle single value or array from URL
  addressVerified: z.preprocess((val) => {
    if (val === undefined || val === null) return undefined;
    const arr = Array.isArray(val) ? val : [val];
    return arr.map((v) => (typeof v === 'string' ? v === 'true' : v));
  }, z.array(z.boolean()).optional()),

  // Status filters - handle single value or array from URL
  statuses: z.preprocess(
    (val) => {
      if (val === undefined || val === null) return undefined;
      return Array.isArray(val) ? val : [val];
    },
    z.array(z.enum(PROVIDER_GROUP_STATUSES)).optional(),
  ),

  // Network status filters
  networkStatuses: z.preprocess(
    (val) => {
      if (val === undefined || val === null) return undefined;
      return Array.isArray(val) ? val : [val];
    },
    z.array(z.enum(['in', 'out', 'unspecified'])).optional(),
  ),

  // Component filters - handle single value or array from URL
  componentIds: z.preprocess((val) => {
    if (val === undefined || val === null) return undefined;
    return Array.isArray(val) ? val : [val];
  }, z.array(z.string()).optional()),
  componentStatuses: z.preprocess(
    (val) => {
      if (val === undefined || val === null) return undefined;
      return Array.isArray(val) ? val : [val];
    },
    z.array(z.enum(['enabled', 'disabled', 'unknown', 'suspended'])).optional(),
  ),
  componentMatchMode: z.enum(['any', 'all']).default('any'),

  // Feature filters - handle single value or array from URL
  featureIds: z.preprocess((val) => {
    if (val === undefined || val === null) return undefined;
    return Array.isArray(val) ? val : [val];
  }, z.array(z.string()).optional()),
  featureStatuses: z.preprocess(
    (val) => {
      if (val === undefined || val === null) return undefined;
      return Array.isArray(val) ? val : [val];
    },
    z.array(z.enum(['enabled', 'disabled', 'unknown'])).optional(),
  ),
  featureMatchMode: z.enum(['any', 'all']).default('any'),

  // Location filter - handle both object (new) and string (old URLs for backwards compat)
  location: z.preprocess(
    (val) => {
      // If it's already an object, return it
      if (typeof val === 'object' && val !== null) return val;
      // If it's a string (old URL format), parse it
      if (typeof val === 'string') {
        try {
          return JSON.parse(val);
        } catch {
          return undefined;
        }
      }
      return undefined;
    },
    z
      .object({
        centerAddress: z.string().optional(),
        locationFilterMode: z.enum(['county', 'city', 'distance']).optional(),
        filterCounty: z.string().nullable().optional(),
        filterCity: z.string().nullable().optional(),
        radiusMiles: z.number().nullable().optional(),
        distanceType: z.enum(['straight', 'driving']).optional(),
      })
      .optional(),
  ),

  // Geographic filters - COORDINATES STORED DIRECTLY - handle single value or array from URL
  centerCoordinates: z.preprocess(
    (val) => {
      if (val === undefined || val === null) return undefined;
      return Array.isArray(val) ? val : [val];
    },
    z
      .array(
        z.object({
          lat: z.coerce.number().min(-90).max(90),
          lng: z.coerce.number().min(-180).max(180),
        }),
      )
      .optional(),
  ),

  // Whether the user has triggered a search via the explicit Search button.
  // The clinics list is only fetched when this is true; on filter changes
  // we drop this flag, so the user must click Search again.
  searched: z
    .union([z.boolean(), z.string()])
    .transform((val) => (typeof val === 'string' ? val === 'true' : val))
    .optional(),

  // Sort state
  sortBy: z.enum(PROVIDER_NETWORK_SORT_BY).default('clinicName'),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),

  // Session-only "extra markers" overlay (proposal contrast dots). Color and
  // size are tracked globally as separate URL params (extraMarkerColor /
  // extraMarkerSize) so the user can recolor or resize all dots at once;
  // each entry here only carries the address + coordinates. zod's default
  // `.strip()` silently drops legacy per-marker `c`/`s` fields from older
  // shared URLs.
  extraMarkers: z.preprocess(
    (val) => {
      if (val === undefined || val === null) return undefined;
      if (typeof val === 'string') {
        try {
          return JSON.parse(val);
        } catch {
          return undefined;
        }
      }
      return val;
    },
    z
      .array(
        z.object({
          a: z.string(),
          lat: z.coerce.number().min(-90).max(90),
          lng: z.coerce.number().min(-180).max(180),
        }),
      )
      .optional(),
  ),

  // Global color/size for the extra markers, omitted from the URL at their
  // defaults (orange / md) to keep links clean.
  extraMarkerColor: z
    .enum([
      'orange',
      'purple',
      'teal',
      'yellow',
      'pink',
      'cyan',
      'lime',
      'emerald',
      'indigo',
      'brown',
      'slate',
    ])
    .optional(),
  extraMarkerSize: z.enum(['sm', 'md', 'lg']).optional(),

  // User-controlled size ramp for the clinic dots. Omitted from the URL when
  // at the default ('sm') so links stay clean; bumped to 'md'/'lg' when the
  // user wants larger dots for low-density regions like Alaska.
  clinicMarkerSize: z.enum(['sm', 'md', 'lg']).optional(),
});

export const Route = createFileRoute('/provider-network/clinics/')({
  component: ClinicsPage,
  validateSearch: clinicsSearchSchema,
});
